import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle, Building2, Home, Calendar, MapPin, FileCheck, Lightbulb, Zap, Sun, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const CaseStudySticklepathSAP = () => {
  const fabricRows = [
    { element: 'Walls — new extension', construction: 'Insulated timber frame', uValue: '0.16 W/m²K', rating: 'good' },
    { element: 'Walls — existing stone', construction: 'Internal insulated lining with cavity drain', uValue: '0.16 W/m²K', rating: 'good' },
    { element: 'Roof — sloping', construction: '140 mm Celotex between rafters + 50 mm counter-battened Celotex', uValue: '0.14 W/m²K', rating: 'good' },
    { element: 'Roof — flat', construction: 'Insulated flat roof section', uValue: '0.13 W/m²K', rating: 'good' },
    { element: 'Ground floor', construction: '110 mm Celotex below concrete slab with radon barrier', uValue: '0.13 W/m²K', rating: 'good' },
    { element: 'Windows', construction: 'Double glazed, argon-filled, uPVC frame', uValue: '1.3 W/m²K', rating: 'ok' },
    { element: 'External doors', construction: 'Solid timber, Part L compliant', uValue: '1.8 W/m²K', rating: 'ok' },
  ];

  const mechRows = [
    { system: 'Space heating', spec: 'Electric panel heaters with programmer and thermostats', rating: 'poor' },
    { system: 'Hot water', spec: '3 kW instantaneous electric under-sink heater, standard tariff', rating: 'poor' },
    { system: 'Ventilation', spec: '2× intermittent extract fans (kitchen & bathroom) + rigid duct', rating: 'ok' },
    { system: 'Lighting', spec: 'All LED throughout (pendant, wall light, spotlight)', rating: 'good' },
    { system: 'Renewables', spec: 'None at assessment date', rating: 'none' },
  ];

  const takeaways = [
    { label: 'Engage early', text: 'A SAP assessor involved at design stage can identify low-cost changes that significantly improve the final rating. Once the build is complete, that opportunity is gone.' },
    { label: 'Fabric first', text: 'Outstanding wall, roof, and floor U-values are the best long-term foundation. Once built they cannot be changed cheaply — and this project got them right.' },
    { label: 'Heating matters most', text: 'Even with exceptional fabric, standard electric panel heaters carry a significant SAP penalty. High heat-retention storage heaters or a heat pump are worth considering at design stage, not after completion.' },
    { label: 'Accurate product data', text: 'Default SAP values are often pessimistic. Manufacturer performance data — particularly for doors and windows — can make the difference between passing and failing MEES.' },
    { label: 'Confirm the build-up', text: 'As-built specifications can differ from drawings. Confirming actual installed insulation thicknesses before submitting data avoids delays at the point of sign-off.' },
    { label: 'Air pressure testing', text: 'Conversions do not require an air pressure test, but the default design value of 15 m³/hr/m² @ 50 Pa is conservative. A test on a property this size could improve the rating at relatively low cost.' },
  ];

  const pillStyles = {
    good: 'bg-green-100 text-green-800',
    ok: 'bg-yellow-100 text-yellow-800',
    poor: 'bg-red-100 text-red-800',
    none: 'bg-gray-100 text-gray-600',
  };
  const pillLabels = { good: 'Very good', ok: 'Good', poor: 'Very poor', none: '—' };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'SAP Case Study: Devon Stone Outbuilding Conversion to 1-Bed Holiday Let — As-Built EPC E (40)',
    description: 'How Energy Performance Direct delivered a retrospective as-built SAP 10.2 calculation and lodged the final EPC for a 36.1 m² stone outbuilding conversion in Sticklepath, Devon, unlocking Building Control sign-off.',
    author: { '@type': 'Organization', name: 'Energy Performance Direct' },
    publisher: { '@type': 'Organization', name: 'Energy Performance Direct', url: 'https://energyperformancedirect.co.uk' },
    datePublished: '2026-05-11',
    image: 'https://energyperformancedirect.co.uk/case-studies/sticklepath/floor-plans.png',
  };

  return (
    <>
      <Helmet>
        <title>SAP Case Study | Devon Stone Outbuilding Conversion | As-Built EPC E (40) | Energy Performance Direct</title>
        <meta name="description" content="SAP 10.2 case study: retrospective as-built EPC for a 36.1 m² Devon stone outbuilding converted into a 1-bed holiday let. Final rating E (40) — MEES compliant. Engaging an assessor at design stage could have unlocked Band B." />
        <meta name="keywords" content="SAP case study Devon, as-built SAP calculation, EPC for conversion, holiday let EPC, stone outbuilding conversion EPC, SAP 10.2 case study, Building Control Part L1b, retrospective SAP, Sticklepath Devon EPC" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/case-study-sticklepath-sap-conversion" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Link to="/case-studies" className="text-sm text-green-600 hover:text-green-700 inline-flex items-center gap-1 mb-4">
                ← All case studies
              </Link>
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Case Study — SAP Assessment &amp; EPC</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Converting a Devon Stone Outbuilding into a One-Bedroom Holiday Let
              </h1>
              <p className="text-lg text-gray-700 mb-6">
                A walk-through of the SAP calculation process, fabric performance, technical challenges, and final EPC outcome for an as-built conversion in rural Devon.
              </p>

              {/* Meta strip */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-8">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Property type</div>
                  <div className="font-semibold text-gray-900 text-sm">Outbuilding conversion</div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Use</div>
                  <div className="font-semibold text-gray-900 text-sm">1-bed holiday let</div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Location</div>
                  <div className="font-semibold text-gray-900 text-sm">Sticklepath, Devon</div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Floor area</div>
                  <div className="font-semibold text-gray-900 text-sm">36.1 m²</div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">SAP version</div>
                  <div className="font-semibold text-gray-900 text-sm">SAP 10.2</div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">EPC lodged</div>
                  <div className="font-semibold text-gray-900 text-sm">May 2026</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <article className="max-w-4xl mx-auto prose prose-lg text-gray-700 space-y-8">

              {/* Background */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Background</h2>
                <p>A property owner in Sticklepath, Devon, commissioned the conversion of a pair of derelict stone outbuildings into a compact one-bedroom holiday let. The project involved extending the original stone structure with a new timber-frame extension and replacing an existing lean-to store, producing a combined dwelling with a total floor area of just 36.1 m².</p>
                <p>Energy Performance Direct was engaged <em>after</em> the build was complete — a retrospective as-built SAP calculation required to satisfy Building Regulations Part L1b and unlock the Building Control sign-off certificate. Devon Building Control had already conducted inspections at drainage, floor, roofing structure, and insulation stages and was satisfied with the build. The EPC was the final outstanding requirement before the certificate could be formally issued.</p>
                <p>This is an important distinction: the assessor had no input into the design or specification decisions made during the project. The fabric and mechanical systems were already fixed by the time the assessment began.</p>
              </div>

              {/* Site plan */}
              <figure className="not-prose">
                <img src="/case-studies/sticklepath/site-plan.png" alt="Proposed site plan — Devon stone outbuilding conversion, Sticklepath" className="w-full rounded-xl border border-gray-200 shadow-md" data-testid="img-site-plan" />
                <figcaption className="text-sm text-gray-500 mt-2 text-center">Proposed Site Plan — architect&rsquo;s drawing submitted with the as-built SAP data.</figcaption>
              </figure>

              {/* EPC outcome */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">EPC outcome</h2>
                <p>
                  The final as-built EPC achieved a rating of <strong>E (40)</strong> — compliant with the current Minimum Energy Efficiency Standard (MEES) for lettable properties — with a potential rating of E (54) once recommended improvements are implemented. The environmental impact rating came in at a strong <strong>B (87)</strong>, reflecting the relatively low carbon intensity of electric heating and the building&rsquo;s efficient fabric.
                </p>

                <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                  <Card className="border-2 border-amber-400 bg-amber-50">
                    <CardContent className="p-6 text-center">
                      <div className="text-sm uppercase tracking-wide text-amber-700 font-semibold mb-2">Current energy efficiency</div>
                      <div className="text-6xl font-extrabold text-amber-600">E</div>
                      <div className="text-sm text-gray-700 mt-2">Score: 40 — MEES compliant</div>
                    </CardContent>
                  </Card>
                  <Card className="border-2 border-green-500 bg-green-50">
                    <CardContent className="p-6 text-center">
                      <div className="text-sm uppercase tracking-wide text-green-700 font-semibold mb-2">Environmental impact</div>
                      <div className="text-6xl font-extrabold text-green-600">B</div>
                      <div className="text-sm text-gray-700 mt-2">Score: 87 — 0.78 t CO₂/year</div>
                    </CardContent>
                  </Card>
                </div>

                <p>Primary energy use stands at <strong>224 kWh/m²/year</strong>, and annual estimated energy costs are in line with typical small all-electric properties of this size.</p>
              </div>

              {/* EPC image */}
              <figure className="not-prose">
                <img src="/case-studies/sticklepath/epc.png" alt="EPC certificate for the Sticklepath holiday let — E (40) current, E (54) potential" className="w-full rounded-xl border border-gray-200 shadow-md" data-testid="img-epc" />
                <figcaption className="text-sm text-gray-500 mt-2 text-center">Lodged EPC certificate — register entry, May 2026.</figcaption>
              </figure>

              {/* Fabric performance */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Fabric performance</h2>
                <p>The project combined the thermal upgrade of the existing random stonework with a new-build timber-frame extension, achieving excellent fabric performance across all elements. The assessor used accredited U-value certificates provided for each construction type.</p>

                <div className="not-prose overflow-x-auto mt-6">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-3 border-b border-gray-200 font-semibold text-gray-700">Element</th>
                        <th className="text-left p-3 border-b border-gray-200 font-semibold text-gray-700">Construction</th>
                        <th className="text-left p-3 border-b border-gray-200 font-semibold text-gray-700">U-value</th>
                        <th className="text-left p-3 border-b border-gray-200 font-semibold text-gray-700">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fabricRows.map((r, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="p-3 font-medium text-gray-900">{r.element}</td>
                          <td className="p-3 text-gray-700">{r.construction}</td>
                          <td className="p-3 text-gray-900 font-medium">{r.uValue}</td>
                          <td className="p-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${pillStyles[r.rating]}`}>{pillLabels[r.rating]}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Floor plans + Elevations */}
              <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-6">
                <figure>
                  <img src="/case-studies/sticklepath/floor-plans.png" alt="Proposed floor plans — Sticklepath stone outbuilding conversion" className="w-full rounded-xl border border-gray-200 shadow-md" data-testid="img-floor-plans" />
                  <figcaption className="text-sm text-gray-500 mt-2 text-center">Proposed Floor Plans</figcaption>
                </figure>
                <figure>
                  <img src="/case-studies/sticklepath/elevations.png" alt="Proposed elevations — Sticklepath stone outbuilding conversion" className="w-full rounded-xl border border-gray-200 shadow-md" data-testid="img-elevations" />
                  <figcaption className="text-sm text-gray-500 mt-2 text-center">Proposed Elevations</figcaption>
                </figure>
              </div>

              {/* Mechanical */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Mechanical &amp; electrical systems</h2>
                <p>As an off-gas-grid rural conversion, all services are all-electric. While the fabric performs well, the choice of heating system has the most significant impact on the SAP score.</p>

                <div className="not-prose overflow-x-auto mt-6">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-3 border-b border-gray-200 font-semibold text-gray-700">System</th>
                        <th className="text-left p-3 border-b border-gray-200 font-semibold text-gray-700">Specification</th>
                        <th className="text-left p-3 border-b border-gray-200 font-semibold text-gray-700">SAP rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mechRows.map((r, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="p-3 font-medium text-gray-900">{r.system}</td>
                          <td className="p-3 text-gray-700">{r.spec}</td>
                          <td className="p-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${pillStyles[r.rating]}`}>{pillLabels[r.rating]}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <blockquote className="not-prose mt-6 p-5 border-l-4 border-green-500 bg-green-50 text-gray-800 italic">
                  &ldquo;The heating system is the primary driver of the lower SAP score. The fabric is genuinely excellent for a conversion — wall-for-wall this building outperforms many new-builds.&rdquo;
                </blockquote>
              </div>

              {/* Sections image */}
              <figure className="not-prose">
                <img src="/case-studies/sticklepath/sections.png" alt="Proposed sections — Sticklepath stone outbuilding conversion" className="w-full rounded-xl border border-gray-200 shadow-md" data-testid="img-sections" />
                <figcaption className="text-sm text-gray-500 mt-2 text-center">Proposed Sections — showing fabric build-up used in U-value calculations.</figcaption>
              </figure>

              {/* Challenges */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Technical challenges during assessment</h2>
                <p>Two issues arose between the draft and final certificate that required careful resolution.</p>

                <div className="not-prose space-y-6 mt-6">
                  <Card className="border-l-4 border-amber-400">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3 mb-3">
                        <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <h3 className="text-lg font-bold text-gray-900">Challenge 1 — External door U-value</h3>
                      </div>
                      <p className="text-gray-700 mb-3">
                        The initial draft EPC had inadvertently included a glazed door in the schedule. When the property owner confirmed both external doors were solid timber, the default SAP U-value of 3.0 W/m²K was applied — which caused the rating to drop below the MEES E threshold, putting Building Control sign-off at risk.
                      </p>
                      <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                        <span className="inline-block text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Resolved</span>
                        <p className="text-sm text-gray-800 m-0">
                          The property owner obtained the manufacturer&rsquo;s Part L specification from the door supplier, confirming a declared U-value of 1.8 W/m²K for the solid timber door. This was submitted to and accepted by the assessor, restoring compliance and returning the rating to E.
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-l-4 border-amber-400">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3 mb-3">
                        <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <h3 className="text-lg font-bold text-gray-900">Challenge 2 — Roof insulation build-up</h3>
                      </div>
                      <p className="text-gray-700 mb-3">
                        A discrepancy was identified in the roof build-up used for the U-value calculation. The initial submission had not fully captured the counter-battened layer of insulation.
                      </p>
                      <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                        <span className="inline-block text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Resolved</span>
                        <p className="text-sm text-gray-800 m-0">
                          The builder confirmed the as-built specification: 140 mm Celotex between rafters, counter-battened with 50 mm battens and 50 mm Celotex infill between the battens. The corrected build-up was calculated at 0.14 W/m²K, an improvement on the initial figure and consistent with the architect&rsquo;s original design intent.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Project timeline</h2>
                <div className="not-prose space-y-4 mt-6">
                  {[
                    { title: 'Design & construction', body: 'Architect drawings prepared and build completed. Devon Building Control made inspections at drainage, floor, roofing structure, and insulation stages. Energy Performance Direct was not involved at this stage.' },
                    { title: 'April 2026 — Practical completion & EPD engaged', body: 'Building Control final site visit completed and deemed satisfactory. Energy Performance Direct instructed to carry out the retrospective as-built SAP calculation required for sign-off.' },
                    { title: 'April 2026 — As-built SAP data submitted', body: 'Completed SAP data form submitted with all supporting documents: U-value certificates, mechanical specs, window schedule, architect drawings, and site/location plan.' },
                    { title: 'May 2026 — Draft EPC issued', body: 'Draft reviewed by the property owner; two technical corrections identified and resolved (door U-value and roof insulation build-up).' },
                    { title: '11 May 2026 — EPC lodged on national register', body: 'Dwelling Sign-Off (DSO) form signed and returned. Certificate lodged with Elmhurst Energy. Building Control sign-off confirmed. EPC valid until May 2036.' },
                  ].map((step, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-green-600 text-white text-sm font-bold flex items-center justify-center">{i + 1}</div>
                        {i < 4 && <div className="w-px flex-1 bg-green-200 min-h-[20px] mt-1" />}
                      </div>
                      <div className="pb-4 flex-1">
                        <h4 className="font-bold text-gray-900">{step.title}</h4>
                        <p className="text-sm text-gray-700 mt-1">{step.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Proactive advice */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Proactive advice — not part of the brief</h2>
                <p>The scope of this instruction was limited to the retrospective as-built SAP and EPC. No design advice was required or requested. However, on reviewing the completed assessment, the assessor voluntarily flagged two straightforward improvements the property owner may wish to consider at the next EPC trigger point (sale or new letting) — neither carries any legal obligation at present.</p>

                <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <Card className="border-2 border-green-200">
                    <CardContent className="p-6">
                      <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Future option 1</div>
                      <Zap className="w-8 h-8 text-green-600 mb-3" />
                      <h3 className="text-lg font-bold text-gray-900 mb-2">High heat-retention storage heaters &amp; Economy 7/10 tariff</h3>
                      <p className="text-sm text-gray-700">Replacing the existing panel heaters with high heat-retention storage heaters on an off-peak tariff would have the single largest positive impact on the SAP score, as the current heating system is the primary drag on the rating.</p>
                      <div className="mt-3 text-xs font-semibold text-green-700 bg-green-50 inline-block px-3 py-1 rounded-full">Largest single improvement available</div>
                    </CardContent>
                  </Card>
                  <Card className="border-2 border-green-200">
                    <CardContent className="p-6">
                      <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Future option 2</div>
                      <Sun className="w-8 h-8 text-green-600 mb-3" />
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Solar PV with battery storage</h3>
                      <p className="text-sm text-gray-700">A roof-mounted PV array with a battery would generate and store on-site electricity, reducing grid dependency for both heating and hot water. Combined with high heat-retention heaters, this would improve the SAP and environmental scores meaningfully.</p>
                      <div className="mt-3 text-xs font-semibold text-green-700 bg-green-50 inline-block px-3 py-1 rounded-full">+9 SAP points • 15.9% CO₂ saving</div>
                    </CardContent>
                  </Card>
                </div>

                <p className="mt-4">Solar water heating was also identified in the standard EPC recommendation report as a further option (+5 SAP points, 8.6% CO₂ reduction), bringing the combined potential rating to <strong>E (54)</strong>.</p>

                <div className="not-prose mt-6 p-6 bg-gradient-to-r from-amber-50 to-green-50 rounded-xl border border-amber-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Current rating</div>
                      <div className="text-2xl font-bold text-amber-600">E 40</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Potential rating</div>
                      <div className="text-2xl font-bold text-amber-600">E 54</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">CO₂ saved</div>
                      <div className="text-2xl font-bold text-green-600">0.2 t</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">2030 MEES</div>
                      <div className="text-xl font-bold text-red-600">C required</div>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-500 mt-3">Note: from 2030, properties in England will require a minimum EPC rating of C to be let. Upgrading to high heat-retention storage heaters on an Economy 7/10 tariff, combined with PV, battery storage and solar thermal, would position this property well ahead of that requirement.</p>
              </div>

              {/* Early involvement */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">What early involvement could have achieved</h2>
                <p>This project is a good example of excellent fabric work let down by system choices that were already fixed by the time the assessor was brought in. The walls, roof, and floor all comfortably exceed current Part L guidance — this is genuinely high-quality construction. But the all-electric panel heating system carries a significant SAP penalty that drags the rating from what could have been a very strong result.</p>
                <p>Had Energy Performance Direct been commissioned at the design stage, when heating system decisions were still open, it would have been straightforward to model alternative approaches — high heat-retention storage heaters on an Economy 7/10 tariff, an air-source heat pump, or a PV-and-battery system. With fabric this good as the starting point, steering the project toward a high B or even an A-rated EPC would have been achievable without a fundamental change to the build specification or budget.</p>
                <p>By the time a retrospective as-built SAP is instructed, those design decisions are fixed. The building is what it is. Early engagement — even a single conversation at concept stage — costs very little and can make a substantial difference to the final certificate.</p>
              </div>

              {/* Key takeaways */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Key takeaways</h2>
                <p>This project illustrates several points that commonly arise on rural conversion EPCs:</p>

                <div className="not-prose mt-6 space-y-3">
                  {takeaways.map((t, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-semibold text-gray-900">{t.label}</div>
                        <p className="text-sm text-gray-700 mt-1">{t.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-6">Case study prepared by Energy Performance Direct · Assessor accredited by Elmhurst Energy Systems Ltd · SAP 10.2 · EPC valid May 2026 – May 2036.</p>
            </article>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-green-600 to-cyan-500">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">Got a conversion or new-build SAP requirement?</h2>
              <p className="text-lg mb-6 opacity-95">Engage us at design stage and we&rsquo;ll model the SAP outcome before the spec is locked in. Or call for a same-week as-built turnaround.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/sap-calculations">
                  <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">SAP Calculations service</Button>
                </Link>
                <Link to="/booking">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                    Get a Quote in 2 Minutes <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" /> 01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CaseStudySticklepathSAP;
